// Strip trailing fractional zeros so `numeric(18,6)` round-trips like
// `'5.000000'` render as `'5'` in editor inputs. Preserves at least one
// digit for the all-zeros case.
export function trimDecimalZeros(value: string): string {
  if (!value.includes('.')) return value;
  return value.replace(/\.?0+$/, '') || '0';
}

/**
 * `a - b`, exactly, on decimal strings.
 *
 * Money shown back to an operator is money they are about to book: a float
 * subtraction that renders 1724.5599999999 next to a field they typed
 * 1234.56 into is not a rounding detail, it is a reason to distrust the
 * screen. Scaling both sides to integers and subtracting with BigInt keeps
 * the arithmetic the same as the ledger's.
 *
 * Returns `null` when either side is not a plain decimal — an empty or
 * half-typed field has no difference to show.
 */
export function subtractDecimals(a: string, b: string, scale = 2): string | null {
  const toScaled = (v: string): bigint | null => {
    const trimmed = v.trim();
    if (!/^\d+(\.\d+)?$/.test(trimmed)) return null;
    const [whole, frac = ''] = trimmed.split('.');
    if (frac.length > scale) return null;
    return BigInt(whole + frac.padEnd(scale, '0'));
  };

  const left = toScaled(a);
  const right = toScaled(b);
  if (left === null || right === null) return null;

  const diff = left - right;
  const negative = diff < 0n;
  const digits = (negative ? -diff : diff).toString().padStart(scale + 1, '0');
  const whole = digits.slice(0, digits.length - scale);
  const frac = scale > 0 ? `.${digits.slice(digits.length - scale)}` : '';
  return `${negative ? '-' : ''}${whole}${frac}`;
}
